import { useEffect, useState } from 'react';
import cl from 'clsx';
import { NavLink, useLocation } from 'react-router';

import LogoImage from '@/shared/assets/images/logo.webp?w=24&inline';
import SynapexaiImage from '@/shared/assets/images/synapexai.svg';

import { ActiveUserBox } from '@/true-fsd/features/ActiveUserBox';
import {
  AdvertiserAccountSelect,
  type AdvertiserAccountSelectProps,
} from '@/true-fsd/features/AdvertiserAccountSelect';
import { WorkspaceSelect } from '@/true-fsd/features/WorkspaceSelect';

import {
  isCreatingNewAccount,
  isCreatingNewWorkspace,
  isEnteringNewAccountName,
  useNewWsStoreIs,
  useAuthorizedOutletContext,
} from '@/true-fsd/data/advertiser-account';
import {
  useAccounts,
  useNewAccountChannelRequests,
  useAccountWorkspaces,
  useAccountChannelRequests,
} from '@/true-fsd/data/advertiser-account/react-query';

import classes from './TopBar.module.scss';

const Logo = () => (
  <NavLink to={'/'} className={classes.logo}>
    <img src={LogoImage} alt="Synapex Logo" />
    <img src={SynapexaiImage} alt="Synapex Text" />
  </NavLink>
);

const useOpenState = (
  close: boolean,
): Pick<AdvertiserAccountSelectProps, 'open' | 'onOpenChange'> => {
  const [open, onOpenChange] = useState(false);

  useEffect(() => {
    if (close) {
      setTimeout(() => onOpenChange(false));
    }
  }, [close]);

  return {
    open,
    onOpenChange,
  };
};

export type TopBarProps = {
  className?: string;
} & Pick<AdvertiserAccountSelectProps, 'RenderNewAccountItem'>;
export const TopBar = ({ className, RenderNewAccountItem }: TopBarProps) => {
  const {
    activeAccount,
    activeWorkspace,
    activeChannelRequest,
    activeNewAccountChannelRequest,
  } = useAuthorizedOutletContext();
  const newAccount = useNewWsStoreIs((state) => state.newAccount);

  const activeAccountOrCr = activeAccount ?? activeNewAccountChannelRequest;

  const location = useLocation();

  const accounts = useAccounts();
  const newAccountChannelRequests = useNewAccountChannelRequests();

  const activeAccountId = activeAccount
    ? activeAccount.advertiser_account_id
    : null;
  const workspaces = useAccountWorkspaces(activeAccountId);
  const channelRequests = useAccountChannelRequests(activeAccountId);

  const accountOpenState = useOpenState(isCreatingNewAccount(location));
  const workspaceOpenState = useOpenState(isCreatingNewWorkspace(location));

  const activeWsOrCr = activeWorkspace ?? activeChannelRequest;

  return (
    <div className={cl(classes.root, className)}>
      <Logo />

      {!isEnteringNewAccountName(location) && (
        <>
          <AdvertiserAccountSelect
            {...accountOpenState}
            RenderNewAccountItem={RenderNewAccountItem}
            accounts={accounts}
            channelRequests={newAccountChannelRequests}
            newAccount={newAccount}
            activeAccount={activeAccountOrCr}
          />

          {activeWsOrCr && (
            <WorkspaceSelect
              {...workspaceOpenState}
              activeAccount={activeAccount}
              workspaces={workspaces}
              channelRequests={channelRequests}
              activeWorkspace={activeWsOrCr}
            />
          )}
        </>
      )}

      <div className={classes.placeholder} />

      <ActiveUserBox />
    </div>
  );
};
